import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Card, Alert } from 'react-bootstrap';
import { motion } from 'framer-motion';
import {
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaFacebook,
  FaInstagram,
  FaWhatsapp,
} from 'react-icons/fa';
import emailjs from '@emailjs/browser';
import IconWrapper from './IconWrapper';
import './Contact.css';

// Doporučeno načítat z .env
const EMAILJS_SERVICE_ID =
  process.env.REACT_APP_EMAILJS_SERVICE_ID || 'service_xxxxx';
const EMAILJS_TEMPLATE_ID =
  process.env.REACT_APP_EMAILJS_TEMPLATE_ID || 'template_xxxxx';
const EMAILJS_PUBLIC_KEY =
  process.env.REACT_APP_EMAILJS_PUBLIC_KEY || 'public_xxxxx';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    date: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setIsSubmitting(true);

    try {
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '-',
        service: formData.service || '-',
        date: formData.date || '-',
        message: formData.message || '-',
        to_name: 'Ivana',
      };

      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        { publicKey: EMAILJS_PUBLIC_KEY }
      );

      setShowSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        date: '',
        message: '',
      });
      setTimeout(() => setShowSuccess(false), 6000);
    } catch (err) {
      console.error(err);
      setErrorMsg('Odeslání se nepodařilo. Zkuste to prosím znovu, nebo napište přímo na e-mail.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      //icon: FaPhone,
      title: 'Telefon',
      value: '+420 725 121 408',
      link: 'tel:+420725121408',
    },
    {
      //icon: FaEnvelope,
      title: 'Email',
      value: 'ivanabuzkova@seznam.cz',
      link: 'mailto:ivanabuzkova@seznam.cz',
    },
    {
      //icon: FaMapMarkerAlt,
      title: 'Adresa',
      value: 'Frýdek-Místek',
      link: 'https://maps.google.com/?q=Fr%C3%BDdek-M%C3%ADstek',
    },
    {
      title: 'IČO',
      value: '04565711',
    },
  ];

  const socialLinks = [
    { icon: FaFacebook, name: 'Facebook', url: 'https://www.facebook.com/fajnefotky' },
    { icon: FaInstagram, name: 'Instagram', url: 'https://www.instagram.com/ivanabuzkova/' },
    { icon: FaWhatsapp, name: 'WhatsApp', url: 'https://wa.me/420725121408' },
  ];

  return (
    <div className="contact-page">
      <div className="contact-hero">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="contact-title">Kontakt</h1>
            <p className="contact-subtitle">
              Rezervujte si termín nebo se mě zeptejte na cokoliv.
            </p>
          </motion.div>
        </Container>
      </div>

      <Container className="py-5">
        <Row className="g-4 align-items-stretch">
          {/* Formulář */}
          <Col lg={8} md={7} className="d-flex">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="flex-fill d-flex"
            >
              <Card className="contact-form-card flex-fill">
                <Card.Body>
                  <h2 className="form-title">Předběžná rezervace termínu</h2>

                  {showSuccess && (
                    <Alert
                      variant="success"
                      onClose={() => setShowSuccess(false)}
                      dismissible
                    >
                      Děkuji za vaši zprávu. Odpovím hned, jakmile to bude možné.
                    </Alert>
                  )}

                  {errorMsg && (
                    <Alert
                      variant="danger"
                      onClose={() => setErrorMsg(null)}
                      dismissible
                    >
                      {errorMsg}
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit} noValidate>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Jméno a příjmení *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            required
                            placeholder="Vaše jméno"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Email *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            required
                            placeholder="vas@email.cz"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Telefon</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+420 ... ... ..."
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label>Služba</Form.Label>
                          <Form.Select
                            name="service"
                            value={formData.service}
                            onChange={handleInputChange}
                          >
                            <option value="">Vyberte službu</option>
                            <option value="Malá svatba">Malá svatba</option>
                            <option value="Střední svatba">Střední svatba</option>
                            <option value="Velká svatba">Velká svatba</option>
                            <option value="Předsvatební focení">Předsvatební focení</option>
                            <option value="Rodinné focení">Rodinné focení</option>
                            <option value="Vánoční focení">Vánoční focení</option>
                            <option value="Školní foto">Školní foto</option>
                            <option value="Focení událostí">Focení událostí</option>
                            <option value="Firemní focení">Firemní focení</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label>Preferované datum</Form.Label>
                      <Form.Control
                        type="date"
                        name="date"
                        value={formData.date}
                        onChange={handleInputChange}
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Zpráva</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Popište svůj požadavek..."
                      />
                    </Form.Group>

                    <Button
                      type="submit"
                      variant="primary"
                      size="lg"
                      disabled={isSubmitting}
                      className="submit-btn"
                    >
                      {isSubmitting ? 'Odesílám...' : 'Odeslat'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>

          {/* Kontaktní informace */}
          <Col lg={4} md={5} className="d-flex">
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
              className="flex-fill d-flex"
            >
              <Card className="contact-info-card flex-fill">
                <Card.Body className="contact-info-body">
                  <h3 className="contact-info-title">Kontaktní informace</h3>

                  {contactInfo.map((info, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="contact-info-item"
                    >
                      {/* <div className="contact-icon">
                        <IconWrapper icon={info.icon} />
                      </div> */}
                      <div className="contact-details">
                        <h5>{info.title}</h5>
                        {info.link ? (
                          <a href={info.link} target="_blank" rel="noopener noreferrer">
                            {info.value}
                          </a>
                        ) : (
                          <span>{info.value}</span>
                        )}
                      </div>
                    </motion.div>
                  ))}

                  <div className="social-links-section mt-4">
                    <h5>Sociální sítě</h5>
                    <div className="social-links">
                      {socialLinks.map((social, index) => (
                        <motion.a
                          key={index}
                          href={social.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="social-link"
                          whileHover={{ scale: 1.08 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <IconWrapper icon={social.icon} />
                        </motion.a>
                      ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </motion.div>
          </Col>
        </Row>

        {/* Mapa – volitelně zapnout */}
        {/* <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="map-section mt-5"
        >
          <Card className="map-card">
            <Card.Body>
              <h3 className="text-center mb-4">Kde mě najdete</h3>
              <div className="map-embed">
                <iframe
                  title="Mapa – FajneFotky"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  src={`https://www.google.com/maps?q=${encodeURIComponent(
                    'Bohuslava Martinů 2425, 738 01 Frýdek-Místek'
                  )}&output=embed`}
                />
              </div>
            </Card.Body>
          </Card>
        </motion.div> */}
      </Container>
    </div>
  );
};

export default Contact;